/**
 * MobileJobsDashboard — mechanic's mobile tool for workshop floor use.
 *
 * Two screens:
 *   1. Job list     — status-tabbed card list with SLO health at a glance
 *   2. Job detail   — full-screen view reusing existing WorkItemCard,
 *                     ObservationRow, AddWorkItemModal, JobNotesTab
 *
 * All actions the mechanic needs:
 *   - Start / Pause / Resume / Complete jobs
 *   - Record measurement observations (slider + text)
 *   - Toggle work item completion checkboxes
 *   - Add / edit work items
 *   - Add notes with photos
 *
 * Receives the same Redux state + dispatch callbacks as the desktop JobsPage.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import SegmentedControl from './mobile/SegmentedControl';
import PagedSearchControls from './PagedSearchControls';
import { filterByNameOrRego } from '../utils/localSearch';
import { useServerSearchTerm } from '../hooks/useSearchBox';
import { QUEUED_COLUMN_STATUSES } from '../utils/queuedStatuses';
import { useDispatch, useSelector } from 'react-redux';
import {
    Wrench, Clock, User, Car, AlertTriangle, Pause, Play, ChevronDown,
    ChevronUp, RefreshCw, Activity, Hash, X, Plus, CheckCircle, CheckCheck,
    MessageSquare, ClipboardList, Trash2, Send, CalendarPlus,
} from 'lucide-react';
import {
    WorkItemCard, AddWorkItemModal, JobApprovalCard, BatchApprovalCard, ApprovalHistoryList,
    isApprovable,
} from './JobDetailModal';
import JobNotesTab from './JobNotesTab';
import VehicleReceivedModal from './VehicleReceivedModal';
import { fetchLoanCarImageCategories } from '../store/slices/bookingSlice';
import { jobDescriptionText } from '../utils/jobDescription';
import {
    fetchJob,
    fetchJobResources,
    fetchAvailableResources,
    purgeJob,
    startAllWorkItems,
    completeAllWorkItems,
    scheduleJob,
    markJobVehicleReceived,
} from '../store/slices/jobSlice';
import PullToRefresh from './mobile/PullToRefresh';

// ── Helpers ───────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = ['COMPLETED', 'INVOICED', 'VEHICLE_COLLECTED', 'CANCELLED'];

const getLiveActiveSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    const base = job.activeSeconds ?? 0;
    if (job.status === 'IN_PROGRESS' && job.updatedAt) {
        return base + Math.max(0, (Date.now() - new Date(job.updatedAt).getTime()) / 1000);
    }
    return base;
};

const getLivePausedSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    let paused = job.pausedDurationSeconds ?? 0;
    if (job.status === 'PAUSED' && job.updatedAt) {
        paused += Math.max(0, (Date.now() - new Date(job.updatedAt).getTime()) / 1000);
    }
    return paused;
};

const getSloStatus = (activeSeconds, sloBudgetSeconds) => {
    if (!sloBudgetSeconds) return 'ok';
    const pct = (activeSeconds / sloBudgetSeconds) * 100;
    if (pct >= 100) return 'overdue';
    if (pct >= 75)  return 'at-risk';
    return 'ok';
};

const fmtDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

// Backend serialises vehicleDescription as `${year} ${make} ${model}`, which
// surfaces a stray leading "0 " when the year is null/0 (e.g. "0 Honda Civic").
// Strip the placeholder so the header reads cleanly.
const formatVehicleDescription = (desc) => {
    if (!desc) return desc;
    return desc.replace(/^0+\s+/, '').trim();
};

/**
 * Sum a work item's cost detail: labour value + sum of part line values.
 * Null-safe at every level. Mirrors the WorkItemCostDetail shape used by
 * the desktop JobDetailModal so the mobile list orders work items the same
 * way (priced items first, $0 inspection/check items last).
 */
const workItemTotalMobile = (item) => {
    if (!item || !item.costDetail) return 0;
    const labour = Number(item.costDetail.labour?.value) || 0;
    const parts = (item.costDetail.parts ?? []).reduce(
        (s, p) => s + (Number(p.value) || 0),
        0,
    );
    return labour + parts;
};

// ── MobileConfirmDialog ───────────────────────────────────────────────────────
//
// In-app replacement for window.confirm — the native iOS/Android browser
// dialog looks out of place on the workshop floor and the OK/Cancel labels
// can't be customised. Styled as a bottom sheet (which is the platform
// idiom on mobile) rather than a centered modal so it works one-handed.

const MobileConfirmDialog = ({
                                 open,
                                 title = 'Confirm',
                                 message,
                                 confirmLabel = 'Confirm',
                                 cancelLabel = 'Cancel',
                                 variant = 'danger',
                                 onConfirm,
                                 onCancel,
                             }) => {
    if (!open) return null;

    const variantStyles = {
        danger:  { btn: 'bg-red-600 active:bg-red-700 text-white',     icon: 'bg-red-100 text-red-600' },
        warning: { btn: 'bg-amber-500 active:bg-amber-600 text-white', icon: 'bg-amber-100 text-amber-600' },
        primary: { btn: 'bg-violet-600 active:bg-violet-700 text-white', icon: 'bg-violet-100 text-violet-600' },
    };
    const v = variantStyles[variant] || variantStyles.danger;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="px-5 py-4 border-b flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${v.icon}`}>
                        <AlertTriangle size={18} />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                </div>
                <div className="px-5 py-4">
                    <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl sm:rounded-b-2xl flex gap-2">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl active:bg-gray-100 min-h-[48px]"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`flex-1 py-3 text-sm font-semibold rounded-xl min-h-[48px] ${v.btn}`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Column config ─────────────────────────────────────────────────────────────

const COLUMNS = [
    { id: 'in-progress', title: 'In Progress', short: 'Active', statuses: ['IN_PROGRESS'],                    activeBg: 'bg-blue-600',    dotColor: 'bg-blue-500' },
    { id: 'paused',      title: 'Waiting',     short: 'Wait',   statuses: ['PAUSED'],                          activeBg: 'bg-amber-500',   dotColor: 'bg-amber-400' },
    { id: 'queued',      title: 'Queued',       short: 'Queue',  statuses: QUEUED_COLUMN_STATUSES,             activeBg: 'bg-gray-700',    dotColor: 'bg-gray-400' },
    { id: 'completed',   title: 'Completed',    short: 'Done',   statuses: ['COMPLETED'],                      activeBg: 'bg-emerald-600', dotColor: 'bg-emerald-500' },
];

const STATUS_BADGE = {
    CREATED:           { label: 'Created',     bg: 'bg-gray-100',    text: 'text-gray-600' },
    QUOTED:            { label: 'Quoted',      bg: 'bg-blue-100',    text: 'text-blue-700' },
    APPROVED:          { label: 'Approved',    bg: 'bg-indigo-100',  text: 'text-indigo-700' },
    IN_PROGRESS:       { label: 'In Progress', bg: 'bg-blue-100',    text: 'text-blue-700' },
    PAUSED:            { label: 'Paused',      bg: 'bg-amber-100',   text: 'text-amber-700' },
    COMPLETED:         { label: 'Completed',   bg: 'bg-emerald-100', text: 'text-emerald-700' },
    INVOICED:          { label: 'Invoiced',    bg: 'bg-teal-100',    text: 'text-teal-700' },
    CANCELLED:         { label: 'Cancelled',   bg: 'bg-red-100',     text: 'text-red-700' },
    VEHICLE_COLLECTED: { label: 'Collected',   bg: 'bg-sky-100',     text: 'text-sky-700' },
};

const PRIORITY_DOT = {
    URGENT: 'bg-red-500',
    HIGH:   'bg-orange-400',
    NORMAL: 'bg-gray-300',
    LOW:    'bg-blue-300',
};

// ── SLO Progress Bar ──────────────────────────────────────────────────────────

const SloBar = ({ activeSeconds, budgetSeconds }) => {
    if (!budgetSeconds) return null;
    const pct = Math.min(100, (activeSeconds / budgetSeconds) * 100);
    const status = getSloStatus(activeSeconds, budgetSeconds);
    const barColor = status === 'overdue' ? 'bg-red-500' : status === 'at-risk' ? 'bg-amber-400' : 'bg-emerald-500';
    return (
        <div className="mt-2">
            <div className="flex items-center justify-between text-[10px] text-gray-400 mb-0.5">
                <span>{fmtDuration(activeSeconds)}</span>
                <span>{fmtDuration(budgetSeconds)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                     style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
        </div>
    );
};

// ── Job List Card ─────────────────────────────────────────────────────────────

const JobListCard = ({ job, onOpen }) => {
    const activeSeconds = getLiveActiveSeconds(job);
    const pausedSeconds = getLivePausedSeconds(job);
    const sloStatus = getSloStatus(activeSeconds, job.sloBudgetSeconds);
    const isOverdue = job.overdue === true || sloStatus === 'overdue';
    const isAwaitingConfirmation = job.status === 'PAUSED' && job.hasAwaitingConfirmation === true;
    const statusBadge = STATUS_BADGE[job.status] || STATUS_BADGE.CREATED;

    const borderColor =
        isAwaitingConfirmation ? 'border-l-yellow-400' :
            isOverdue ? 'border-l-red-500' :
                sloStatus === 'at-risk' ? 'border-l-amber-400' :
                    'border-l-emerald-500';

    return (
        <button
            onClick={() => onOpen(job)}
            className={`w-full text-left rounded-xl border-l-4 bg-white shadow-sm active:shadow-md transition-all p-3.5 ${borderColor} ${isOverdue ? 'ring-1 ring-red-200' : ''}`}
        >
            {/* Row 1: customer + priority + status */}
            <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[job.priority] || PRIORITY_DOT.NORMAL}`} />
                    <span className="font-semibold text-gray-900 text-sm truncate">{job.customerName}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {job.priority === 'URGENT' && (
                        <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">
                            <AlertTriangle size={9} /> URGENT
                        </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                        {statusBadge.label}
                    </span>
                </div>
            </div>

            {/* Row 2: vehicle + job number */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="flex items-center gap-1 text-xs text-gray-500 truncate">
                    <Car size={11} className="shrink-0" />
                    {formatVehicleDescription(job.vehicleDescription) || job.vehicleRego}
                </span>
                <span className="text-[10px] text-gray-400 font-mono shrink-0">{job.jobNumber}</span>
            </div>

            {/* Row 3: staff + timers */}
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1 text-xs text-gray-500">
                    <User size={10} className="shrink-0" />
                    <span className="truncate max-w-[100px]">{job.assignedStaffName || 'Unassigned'}</span>
                </span>
                <div className="flex items-center gap-2">
                    {activeSeconds > 0 && (
                        <span className="flex items-center gap-0.5 text-xs font-mono font-semibold text-emerald-600">
                            <Clock size={10} /> {fmtDuration(activeSeconds)}
                        </span>
                    )}
                    {pausedSeconds > 60 && (
                        <span className="flex items-center gap-0.5 text-xs font-mono text-amber-500">
                            <Pause size={9} /> {fmtDuration(pausedSeconds)}
                        </span>
                    )}
                    {job.workItemCount > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                            <Wrench size={10} /> {job.workItemCount}
                        </span>
                    )}
                </div>
            </div>

            {/* Awaiting confirmation */}
            {isAwaitingConfirmation && (
                <div className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <Clock size={10} className="text-yellow-600" />
                    <span className="text-[11px] font-semibold text-yellow-700">Awaiting customer approval</span>
                </div>
            )}

            {/* SLO bar */}
            <SloBar activeSeconds={activeSeconds} budgetSeconds={job.sloBudgetSeconds} />
        </button>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// ── Mobile Notes Tab ─────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
//
// Phone-tuned notes panel. Measurement recording has moved to the Work items
// tab — each WorkItemCard now carries big IN / OUT buttons that open a focused
// recorder popup, which is the right pattern for phones. So this tab is now
// purely the note composer + feed, delegating entirely to JobNotesTab.

const MobileNotesTab = ({
                            job,
                            isEditable,
                            isWorkItemEditable,
                            operationLoading,
                            dispatch,
                            onAddNote,
                            onRefreshJob,
                        }) => {
    return (
        <div className="space-y-3">
            <JobNotesTab
                job={job}
                onNoteAdded={onAddNote}
                onRefreshJob={onRefreshJob}
                operationLoading={operationLoading}
                compact
            />
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
// ── Mobile Job Detail (full-screen) ──────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

/**
 * The full-screen job detail on a phone.
 *
 * Exported because a QUOTE is a job: the phone's quotes screen opens this same
 * component to price one up, rather than growing a second work-item editor that would
 * accept different things from the same API. See MobileQuotesPage.
 */
export const MobileJobDetail = ({
                             job,
                             operationLoading,
                             // Only supplied by the quotes screen. A quote awaiting the
                             // customer has no lifecycle buttons — see the footer.
                             onSendQuote,
                             workCategories,
                             categoryTypes,
                             onClose,
                             onStartJob,
                             onPauseJob,
                             onResumeJob,
                             onOpenComplete,
                             onAddWorkItem,
                             onEditWorkItem,
                             onRemoveWorkItem,
                             onAddNote,
                             onRefreshJob,
                         }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('work');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    // Work-item removal goes through MobileConfirmDialog rather than the
    // browser's window.confirm so the prompt is styled consistently and reads
    // as a bottom sheet (the native pattern on mobile).
    const [pendingRemoveUuid, setPendingRemoveUuid] = useState(null);

    // Delete-job confirm dialog state — type-to-confirm gate matching the
    // desktop/tablet flow. Lives local because it's purely UI; the actual
    // purge dispatch goes via the shared slice thunk.
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteJobInput,   setDeleteJobInput]   = useState('');

    // Editability is unconditionally true to mirror the desktop JobDetailModal —
    // backend authorisation rules remain the source of truth for what's actually
    // permitted. This lets mechanics fix typos on completed jobs or record a
    // missed observation after the fact from their phone.
    const isEditable         = true;
    const isWorkItemEditable = true;

    // A quote the customer has not answered yet. Read off the job — the same two
    // server-owned fields the desktop JobDetailModal uses — not off which screen is
    // rendering, so this component behaves identically for both its callers.
    const isPendingQuote = !!job.quote && job.status === 'QUOTED';

    // Same two rules as the desktop JobDetailModal, read off the job rather than off
    // which screen is rendering it: a job with no appointment can be booked in, and a
    // booked job whose car has not arrived can be received.
    const needsBooking = !isPendingQuote && !job.bookingUuid
        && ['CREATED', 'QUOTED'].includes(job.status);
    const awaitingVehicle = !!job.bookingUuid && job.bookingStatus === 'SCHEDULED';

    const [showBookingSheet, setShowBookingSheet] = useState(false);
    const [showHandoverModal, setShowHandoverModal] = useState(false);

    // Server-driven photo categories, fetched only for a job that could need them.
    const loanCarImageCategories = useSelector((s) => s.bookings?.loanCarImageCategories ?? []);
    useEffect(() => {
        if (job.bookingHasLoanCar && loanCarImageCategories.length === 0) {
            dispatch(fetchLoanCarImageCategories());
        }
    }, [dispatch, job.bookingHasLoanCar, loanCarImageCategories.length]);
    const [bookingDate, setBookingDate] = useState('');
    const [bookingTime, setBookingTime] = useState('');
    const [bookingError, setBookingError] = useState(null);

    const handleBookIn = async () => {
        setBookingError(null);
        const result = await dispatch(scheduleJob({
            jobUuid: job.uuid, scheduledDate: bookingDate, scheduledTime: bookingTime || null,
        }));
        if (scheduleJob.rejected.match(result)) {
            setBookingError(result.payload);
            return;
        }
        setShowBookingSheet(false);
        setBookingDate('');
        setBookingTime('');
        onRefreshJob?.();
    };

    // ⚠️ Same rule as the desktop card: a booking with a loan car cannot be received
    // with an empty handover, and showing that rejection on a phone is a dead end —
    // the form it asks for is on another screen. Open it instead.
    const handleVehicleReceived = async () => {
        if (job.bookingHasLoanCar) {
            setShowHandoverModal(true);
            return;
        }
        const result = await dispatch(markJobVehicleReceived({ jobUuid: job.uuid }));
        if (markJobVehicleReceived.fulfilled.match(result)) onRefreshJob?.();
    };

    const handleHandoverConfirm = async ({ loanCarDetails }) => {
        const result = await dispatch(markJobVehicleReceived({ jobUuid: job.uuid, loanCarDetails }));
        if (markJobVehicleReceived.fulfilled.match(result)) {
            setShowHandoverModal(false);
            onRefreshJob?.();
        }
    };
    // The server refuses to send a quote with nothing priced on it (zero-cost
    // inspection lines alone are not something to authorise). Said on the button
    // rather than left to a failed request; the server stays the authority, and its
    // refusal is surfaced verbatim if the two ever disagree.
    const hasPricedWorkItem = Number(job.totalEstimatedCost ?? 0) > 0;

    const activeSeconds = getLiveActiveSeconds(job);
    const pausedSeconds = getLivePausedSeconds(job);
    const sloStatus = getSloStatus(activeSeconds, job.sloBudgetSeconds);
    const isOverdue = job.overdue === true || sloStatus === 'overdue';
    const statusBadge = STATUS_BADGE[job.status] || STATUS_BADGE.CREATED;

    const handleRemove = (uuid) => { setPendingRemoveUuid(uuid); };
    const handleConfirmRemove = () => {
        const uuid = pendingRemoveUuid;
        setPendingRemoveUuid(null);
        if (uuid) onRemoveWorkItem(uuid);
    };
    const handleOpenEdit = (item) => { setEditingItem(item); setShowEditModal(true); };

    const openDeleteDialog = () => { setDeleteJobInput(''); setShowDeleteDialog(true); };
    const handleConfirmDeleteJob = () => {
        if (deleteJobInput.trim() !== job.jobNumber) return;
        // The slice's purgeJob.fulfilled reducer clears selectedJob and
        // closes showViewModal, so the mobile detail screen unmounts
        // automatically — no explicit onClose() needed here.
        dispatch(purgeJob(job.uuid));
        setShowDeleteDialog(false);
    };

    // Count completed work items
    const workItems = job.workItems || [];
    const completedCount = workItems.filter(i => i.status === 'COMPLETED').length;

    // Priced items first, then $0 inspection/check items. Stable sort
    // (Array.prototype.sort is stable per ES2019), so within each group the
    // backend's sortOrder is preserved. Mirrors the desktop JobDetailModal
    // ordering so all three surfaces show the same list shape. We keep the
    // unsorted `workItems` for count expressions where order is irrelevant.
    const sortedWorkItems = [...workItems]
        .sort((a, b) => (workItemTotalMobile(b) > 0 ? 1 : 0) - (workItemTotalMobile(a) > 0 ? 1 : 0));

    // Bulk Start All / Complete All. Same behaviour as desktop/tablet — one
    // server call per action, server fills OUT measurements with the green/OK
    // value before transitioning items to COMPLETED. Both confirm-gated via the
    // MobileConfirmDialog bottom sheet so a fat-finger doesn't wipe in-progress
    // measurement data.
    const [bulkConfirm, setBulkConfirm] = useState(null); // 'start' | 'complete' | null
    const [bulkBusy, setBulkBusy] = useState(false);
    const pendingCount = workItems.filter(wi => wi.status === 'PENDING').length;
    const nonTerminalCount = workItems
        .filter(wi => wi.status !== 'COMPLETED' && wi.status !== 'DECLINED').length;
    // Hide Start All / Complete All while any work item is still awaiting
    // customer approval. Same gate as desktop / tablet — workshop activity
    // shouldn't outrun the approval flow.
    const hasAwaitingApproval = workItems.some(wi =>
        wi.status === 'AWAITING_CONFIRMATION'
        || (wi.confirmationRequired && (wi.customerConfirmed === null || wi.customerConfirmed === undefined))
    );

    // ── Approvals tab badge ──
    // Same three-way rule as the desktop JobDetailModal: an in-flight request
    // shows an amber dot and no number; otherwise the count of items still
    // eligible for approval; falling back to the size of the audit trail once
    // everything has been decided, so the tab still signals that history exists.
    const eligibleApprovalCount = workItems.filter(isApprovable).length;
    const approvalAwaiting      = job.lastApprovalStatus === 'SENT';
    const approvalHistoryCount  = Array.isArray(job.approvalHistory) ? job.approvalHistory.length : 0;
    const approvalsBadge        = approvalAwaiting
        ? 0
        : (eligibleApprovalCount > 0 ? eligibleApprovalCount : approvalHistoryCount);

    // Mirrors the desktop empty state: nothing left to ask about, nothing in
    // flight, and no history to show. Zero-cost inspection lines are excluded
    // because they never take part in approval at all (see isApprovable), so
    // including them would suppress the empty state on almost every job.
    const approvalsEmpty =
        job.approvalMode !== 'PROFILE'
        && workItems.filter(wi => workItemTotalMobile(wi) > 0)
            .every(wi => wi.customerConfirmed === true || wi.customerConfirmed === false)
        && !approvalAwaiting
        && approvalHistoryCount === 0;

    const handleStartAll = async () => {
        setBulkBusy(true);
        try { await dispatch(startAllWorkItems({ jobUuid: job.uuid })).unwrap(); }
        catch { /* operationError surfaces via the slice toast */ }
        finally { setBulkBusy(false); setBulkConfirm(null); }
    };
    const handleCompleteAll = async () => {
        setBulkBusy(true);
        try { await dispatch(completeAllWorkItems({ jobUuid: job.uuid })).unwrap(); }
        catch { /* operationError surfaces via the slice toast */ }
        finally { setBulkBusy(false); setBulkConfirm(null); }
    };

    return (
        // pt-safe / pb-safe: this screen covers the whole viewport including
        // MobileShell's insets, so it has to re-apply them itself or the job
        // number sits under the notch and the action bar under the iPhone home
        // indicator. Both are 0 outside an installed app.
        <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col pt-safe">
            {/* ── Header ── */}
            <div className="bg-white border-b border-gray-100 px-4 pt-3 pb-2 shrink-0">
                {/* Title row */}
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base font-bold text-gray-900">{job.jobNumber}</h2>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                            {job.customerName} · {formatVehicleDescription(job.vehicleDescription) || job.vehicleRego}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {/* Delete — irreversible. Sits next to Close in the header
                            (not in the sticky bottom bar) so it's reachable but
                            not in the thumb's natural path during workshop use.
                            Gated by the type-to-confirm dialog below. */}
                        <button aria-label="Delete job" onClick={openDeleteDialog}
                                disabled={operationLoading}
                                className="p-2 -mt-1 active:bg-red-50 rounded-xl min-w-[44px] min-h-[44px] flex items-center justify-center disabled:opacity-40"
                                title="Permanently delete this job, its booking and invoice">
                            <Trash2 size={18} className="text-red-500" />
                        </button>
                        {/* Icon-only, so it carries its own name: a screen reader
                            otherwise announces both of these as just "button". */}
                        <button onClick={onClose} aria-label="Close"
                                className="p-2 -mr-2 -mt-1 active:bg-gray-100 rounded-xl min-w-[44px] min-h-[44px] flex items-center justify-center">
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>
                </div>

                {/* Timer strip */}
                <div className="flex items-center gap-3 mb-2">
                    {activeSeconds > 0 && (
                        <span className="flex items-center gap-1 text-xs font-mono font-semibold text-emerald-600">
                            <Clock size={11} /> {fmtDuration(activeSeconds)}
                        </span>
                    )}
                    {pausedSeconds > 60 && (
                        <span className="flex items-center gap-1 text-xs font-mono text-amber-500">
                            <Pause size={10} /> {fmtDuration(pausedSeconds)}
                        </span>
                    )}
                    {job.sloBudgetSeconds > 0 && (
                        <span className={`text-xs font-mono ${isOverdue ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                            / {fmtDuration(job.sloBudgetSeconds)}
                        </span>
                    )}
                    <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
                        <User size={10} /> {job.assignedStaffName || 'Unassigned'}
                    </span>
                </div>

                {/* Tabs.
                    The Approvals badge mirrors the desktop rules exactly (see
                    JobDetailModal's tab block): a count of items still eligible
                    for approval, or an amber dot when a request is already out
                    with the customer, or the history count once everything is
                    decided. Labels are shortened to fit three tabs on a 375px
                    phone; the icons carry most of the meaning at that width.
                    The notes tab is "Photos" here and "Notes & Photos" on desktop: the
                    picture is what staff are looking for on a phone, standing next to
                    the car, and the speech-bubble icon still says notes. Measured — the
                    full desktop label needs 146px against the 111px a third of a 375px
                    row gives, so spelling it out would only ever arrive truncated. */}
                <div className="flex gap-1">
                    {[
                        { id: 'work',      label: 'Work',      icon: ClipboardList, count: workItems.length, badge: workItems.length > 0 ? `${completedCount}/${workItems.length}` : null },
                        { id: 'approvals', label: 'Approvals', icon: Send,          count: approvalsBadge, dot: approvalAwaiting },
                        { id: 'notes',     label: 'Photos',    icon: MessageSquare, count: job.notes?.length || 0 },
                    ].map(tab => {
                        const isActive = activeTab === tab.id;
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors min-h-[44px] ${
                                    isActive ? 'bg-violet-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                <Icon size={14} />
                                {tab.label}
                                {tab.dot && (
                                    <span
                                        className="w-2 h-2 rounded-full bg-amber-400 shrink-0"
                                        title="Awaiting customer response"
                                    />
                                )}
                                {tab.badge && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                        isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {tab.badge}
                                    </span>
                                )}
                                {!tab.badge && tab.count > 0 && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                        isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Scrollable content ── */}
            <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
                {operationLoading && (
                    <div className="flex items-center gap-2 text-sm text-violet-600 bg-violet-50 px-3 py-2 rounded-xl">
                        <RefreshCw size={14} className="animate-spin" /> Updating…
                    </div>
                )}

                {/* ── Work Items Tab ── */}
                {activeTab === 'work' && (
                    <>
                        {/* Job info summary (collapsible) */}
                        <JobInfoSummary job={job} />

                        {/* Bulk actions row — Start All / Complete All. Sits
                            above the Add Work Item dashed button as a compact
                            two-column grid so both fit on a narrow phone width
                            without wrapping. Each button only renders when there's
                            something to act on, so an all-completed job sees neither
                            and the screen stays clean. Hidden entirely while any
                            work item is still awaiting customer approval. */}
                        {isWorkItemEditable && !hasAwaitingApproval && (pendingCount > 0 || nonTerminalCount > 0) && (
                            <div className="grid grid-cols-2 gap-2">
                                {pendingCount > 0 && (
                                    <button
                                        onClick={() => setBulkConfirm('start')}
                                        disabled={bulkBusy || operationLoading}
                                        className={`flex items-center justify-center gap-1.5 py-3 border border-blue-300 rounded-xl text-blue-700 text-sm font-medium active:bg-blue-50 min-h-[48px] disabled:opacity-50 ${nonTerminalCount === 0 ? 'col-span-2' : ''}`}
                                    >
                                        <Play size={14} /> Start All ({pendingCount})
                                    </button>
                                )}
                                {nonTerminalCount > 0 && (
                                    <button
                                        onClick={() => setBulkConfirm('complete')}
                                        disabled={bulkBusy || operationLoading}
                                        className={`flex items-center justify-center gap-1.5 py-3 border border-emerald-300 rounded-xl text-emerald-700 text-sm font-medium active:bg-emerald-50 min-h-[48px] disabled:opacity-50 ${pendingCount === 0 ? 'col-span-2' : ''}`}
                                    >
                                        <CheckCheck size={14} /> Complete All ({nonTerminalCount})
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Add work item button */}
                        {isEditable && (
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-violet-200 rounded-xl text-violet-600 text-sm font-medium active:bg-violet-50 min-h-[48px]"
                            >
                                <Plus size={16} /> Add Work Item
                            </button>
                        )}

                        {/* Work items list — reusing existing WorkItemCard */}
                        {workItems.length === 0 ? (
                            <div className="text-center py-10 text-gray-400">
                                <Wrench size={28} className="mx-auto mb-2 opacity-40" />
                                <p className="text-sm">No work items yet</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {sortedWorkItems.map(item => (
                                    <WorkItemCard
                                        key={item.uuid}
                                        item={item}
                                        job={job}
                                        isEditable={isEditable}
                                        isWorkItemEditable={isWorkItemEditable}
                                        operationLoading={operationLoading}
                                        dispatch={dispatch}
                                        onEdit={() => handleOpenEdit(item)}
                                        onRemove={() => handleRemove(item.uuid)}
                                    />
                                ))}
                            </div>
                        )}

                    </>
                )}

                {/* ── Approvals Tab ──
                    Previously the phone could only ever send a PROFILE-mode
                    job-level request, and only from the bottom of the Work
                    Items list. Non-PROFILE jobs — the ad-hoc ones, where a tech
                    finds extra work mid-inspection and needs the customer to
                    authorise it — had no approval surface on a phone at all,
                    which is exactly the case that happens under the hoist.

                    Both cards are the same components the desktop and tablet
                    render, so the IN-measurement pre-flight warning, the
                    zero-cost exclusion and the single-request-in-flight lock
                    all behave identically here. */}
                {activeTab === 'approvals' && (
                    <>
                        {job.approvalMode === 'PROFILE' ? (
                            <JobApprovalCard
                                job={job}
                                isEditable={isEditable}
                                operationLoading={operationLoading}
                                dispatch={dispatch}
                            />
                        ) : (
                            <BatchApprovalCard
                                job={job}
                                isEditable={isEditable}
                                operationLoading={operationLoading}
                                dispatch={dispatch}
                            />
                        )}

                        <ApprovalHistoryList history={job.approvalHistory} />

                        {approvalsEmpty && (
                            <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-xl border border-gray-100">
                                <Send size={20} className="mx-auto mb-2 opacity-40" />
                                No items awaiting customer approval
                            </div>
                        )}
                    </>
                )}

                {/* ── Notes Tab ──
                    Note composer + feed only. Measurement recording used to
                    live here behind a chip-style work-item picker; it now sits
                    on each WorkItemCard as big IN / OUT buttons, which is one
                    fewer selection step for a tech holding a phone in one hand.
                    See MobileNotesTab above. */}
                {activeTab === 'notes' && (
                    <MobileNotesTab
                        job={job}
                        isEditable={isEditable}
                        isWorkItemEditable={isWorkItemEditable}
                        operationLoading={operationLoading}
                        dispatch={dispatch}
                        onAddNote={onAddNote}
                        onRefreshJob={onRefreshJob}
                    />
                )}
            </div>

            {/* ── Sticky bottom action bar ── */}
            <div className="px-4 pt-3 pb-safe-add bg-white border-t border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                    {/* A quote awaiting the customer is not startable work: nobody has
                        authorised it and the car is not here. Its one action is sending
                        it, whole, with no item selection — the quote IS the thing being
                        authorised. Once the customer answers it becomes a CREATED job
                        and picks up the ordinary buttons. Same rule as the desktop
                        JobDetailModal, and read off the job rather than off which screen
                        is rendering it. */}
                    {needsBooking && (
                        <button onClick={() => setShowBookingSheet(true)}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-white border border-indigo-300 text-indigo-700 rounded-xl text-sm font-semibold active:bg-indigo-50 min-h-[48px]">
                            <CalendarPlus size={16} /> Book in
                        </button>
                    )}
                    {awaitingVehicle && (
                        <button onClick={handleVehicleReceived} disabled={operationLoading}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-sky-600 text-white rounded-xl text-sm font-semibold active:bg-sky-700 disabled:opacity-40 min-h-[48px]">
                            <Car size={16} /> Vehicle Received
                        </button>
                    )}
                    {isPendingQuote && onSendQuote && (
                        <button onClick={() => onSendQuote(job)}
                                disabled={operationLoading || !hasPricedWorkItem}
                                title={hasPricedWorkItem ? 'Send every priced item to the customer' : 'Add a priced work item first'}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-violet-600 text-white rounded-xl text-sm font-semibold active:bg-violet-700 disabled:opacity-40 min-h-[48px]">
                            <Send size={16} /> {operationLoading ? 'Sending…' : 'Send for approval'}
                        </button>
                    )}
                    {!isPendingQuote && ['CREATED', 'QUOTED', 'APPROVED'].includes(job.status) && (
                        <button onClick={() => onStartJob(job)}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold active:bg-blue-700 min-h-[48px]">
                            <Play size={16} /> Start Job
                        </button>
                    )}
                    {job.status === 'IN_PROGRESS' && (
                        <>
                            <button onClick={() => onPauseJob(job)}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold active:bg-amber-600 min-h-[48px]">
                                <Pause size={16} /> Pause
                            </button>
                            <button onClick={() => { onOpenComplete(job); onClose(); }}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-emerald-600 text-white rounded-xl text-sm font-semibold active:bg-emerald-700 min-h-[48px]">
                                <CheckCircle size={16} /> Complete
                            </button>
                        </>
                    )}
                    {job.status === 'PAUSED' && (
                        <button onClick={() => onResumeJob(job)}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold active:bg-blue-700 min-h-[48px]">
                            <Play size={16} /> Resume
                        </button>
                    )}
                    {TERMINAL_STATUSES.includes(job.status) && (
                        <button onClick={onClose}
                                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium active:bg-gray-200 min-h-[48px]">
                            Close
                        </button>
                    )}
                </div>
            </div>

            {/* ── Add Work Item Modal ── */}
            {showAddModal && (
                <AddWorkItemModal
                    job={job}
                    mode="add"
                    onClose={() => setShowAddModal(false)}
                    onSubmit={(payload) => { onAddWorkItem(payload); setShowAddModal(false); }}
                    loading={operationLoading}
                    workCategories={workCategories}
                    categoryTypes={categoryTypes}
                />
            )}

            {/* ── Edit Work Item Modal ── */}
            {showEditModal && editingItem && (
                <AddWorkItemModal
                    job={job}
                    mode="edit"
                    onClose={() => { setShowEditModal(false); setEditingItem(null); }}
                    onSubmit={(payload) => { onEditWorkItem(payload, editingItem.uuid); setShowEditModal(false); setEditingItem(null); }}
                    loading={operationLoading}
                    workCategories={workCategories}
                    categoryTypes={categoryTypes}
                    initialValues={{
                        description: editingItem.description ?? '',
                        detailNotes: editingItem.detailNotes ?? '',
                        workCategory: editingItem.workCategory ?? '',
                        confirmationRequired: editingItem.confirmationRequired ?? false,
                        confirmationMessage: editingItem.confirmationMessage ?? '',
                        status: editingItem.status ?? '',
                        costDetail: editingItem.costDetail ?? null,
                    }}
                />
            )}

            {/* In-app confirmation for work item removal — replaces the
                browser's window.confirm so the prompt reads as a bottom sheet
                and matches the rest of the app's visual language. */}
            <MobileConfirmDialog
                open={pendingRemoveUuid != null}
                title="Remove work item?"
                message="This will remove the work item from the job. This action cannot be undone."
                confirmLabel="Remove"
                cancelLabel="Cancel"
                variant="danger"
                onConfirm={handleConfirmRemove}
                onCancel={() => setPendingRemoveUuid(null)}
            />

            {/* Bulk Start All / Complete All confirmation. The Complete All
                message spells out the OUT auto-fill behaviour because that's
                the bit a tech might not expect on first use. */}
            <MobileConfirmDialog
                open={bulkConfirm != null}
                title={bulkConfirm === 'complete' ? 'Complete all work items?' : 'Start all work items?'}
                message={
                    bulkConfirm === 'complete'
                        ? `This will mark ${nonTerminalCount} work item${nonTerminalCount === 1 ? '' : 's'} as completed and auto-fill every OUT measurement with "OK". Items already completed, declined, or awaiting customer confirmation will be skipped.`
                        : `This will move ${pendingCount} pending work item${pendingCount === 1 ? '' : 's'} into In Progress.`
                }
                confirmLabel={bulkConfirm === 'complete' ? 'Complete All' : 'Start All'}
                cancelLabel="Cancel"
                variant="primary"
                onConfirm={bulkConfirm === 'complete' ? handleCompleteAll : handleStartAll}
                onCancel={() => setBulkConfirm(null)}
            />

            {/* Delete-job type-to-confirm dialog.
                Backed by DELETE /api/jobs/{uuid}/purge — irreversible. The user
                must type the exact job number for the destructive button to
                enable. Centered card rather than a bottom sheet because the
                text input needs the keyboard which itself slides up from the
                bottom — a bottom sheet would be hidden behind it. */}
            {/* Loan car handover — the same form both other screens use, so all three
                paths collect and post identical evidence. */}
            {showHandoverModal && (
                <VehicleReceivedModal
                    booking={{ bookingCode: job.bookingCode, customerName: job.customerName }}
                    imageCategories={loanCarImageCategories}
                    onConfirm={handleHandoverConfirm}
                    onClose={() => setShowHandoverModal(false)}
                    loading={operationLoading}
                    error={null}
                />
            )}

            {/* Book in — a bottom sheet, thumb-reachable. Same two fields as the
                desktop dialog: the customer and vehicle are already on the job, so
                the only thing missing is when. */}
            {showBookingSheet && (
                <div className="fixed inset-0 z-[70] flex items-end bg-black/50"
                     onClick={() => setShowBookingSheet(false)}>
                    <div className="w-full rounded-t-2xl bg-white p-5 pb-8"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" />
                        <h3 className="text-base font-semibold text-gray-900">Book this job in</h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {job.customerName} · {job.vehicleDescription}
                        </p>

                        <label className="mt-4 block">
                            <span className="text-xs font-medium text-gray-600">Date</span>
                            <input type="date" value={bookingDate}
                                   onChange={(e) => setBookingDate(e.target.value)}
                                   className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-3 text-base min-h-[48px]" />
                        </label>

                        <label className="mt-3 block">
                            <span className="text-xs font-medium text-gray-600">
                                Time <span className="font-normal text-gray-400">optional</span>
                            </span>
                            <input type="time" value={bookingTime}
                                   onChange={(e) => setBookingTime(e.target.value)}
                                   className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-3 text-base min-h-[48px]" />
                        </label>

                        {bookingError && (
                            <p className="mt-3 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                                {bookingError}
                            </p>
                        )}

                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setShowBookingSheet(false)}
                                    className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 min-h-[48px]">
                                Cancel
                            </button>
                            <button onClick={handleBookIn} disabled={!bookingDate || operationLoading}
                                    className="flex-1 py-3 rounded-xl bg-indigo-600 text-sm font-semibold text-white disabled:opacity-40 min-h-[48px]">
                                Book in
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDialog && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
                        <div className="px-5 py-4 border-b flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                                <AlertTriangle size={20} className="text-red-600" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base font-bold text-gray-900">Delete job?</h2>
                                <p className="text-xs text-gray-500 mt-0.5">This action is permanent.</p>
                            </div>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 space-y-1">
                                <p className="font-semibold">Will be deleted:</p>
                                <ul className="list-disc pl-4 space-y-0.5">
                                    <li>Job <span className="font-mono">{job.jobNumber}</span> and all work items, notes, observations</li>
                                    <li>Its booking (if any)</li>
                                    <li>Its invoice and line items (if any)</li>
                                </ul>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Type <span className="font-mono font-semibold text-gray-900">{job.jobNumber}</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteJobInput}
                                    onChange={(e) => setDeleteJobInput(e.target.value)}
                                    placeholder={job.jobNumber}
                                    autoFocus
                                    className="w-full border rounded-lg px-3 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[44px]"
                                />
                            </div>
                            <div className="flex gap-2 pt-1">
                                <button
                                    onClick={() => setShowDeleteDialog(false)}
                                    className="flex-1 py-2.5 border rounded-lg active:bg-gray-50 text-sm font-medium min-h-[44px]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmDeleteJob}
                                    disabled={operationLoading || deleteJobInput.trim() !== job.jobNumber}
                                    className="flex-1 py-2.5 bg-red-600 text-white rounded-lg active:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold min-h-[44px]"
                                >
                                    {operationLoading ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Job Info Summary (collapsible on mobile) ──────────────────────────────────

const JobInfoSummary = ({ job }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-3.5 py-2.5 active:bg-gray-50"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <Car size={14} className="text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-700 truncate">
                        {formatVehicleDescription(job.vehicleDescription) || job.vehicleRego}
                    </span>
                    {job.vehicleRego && job.vehicleDescription && (
                        <span className="text-xs text-gray-400 font-mono shrink-0">{job.vehicleRego}</span>
                    )}
                </div>
                {expanded ? <ChevronUp size={16} className="text-gray-300" /> : <ChevronDown size={16} className="text-gray-300" />}
            </button>

            {expanded && (
                <div className="px-3.5 pb-3 pt-0 space-y-2 border-t border-gray-50">
                    <InfoRow label="Customer" value={job.customerName} />
                    {job.customerPhone && <InfoRow label="Phone" value={job.customerPhone} isPhone />}
                    {job.assignedStaffName && <InfoRow label="Assigned" value={job.assignedStaffName} />}
                    {job.scheduledStartDate && <InfoRow label="Scheduled" value={job.scheduledStartDate} />}
                    {job.odometerIn != null && <InfoRow label="Odometer In" value={`${job.odometerIn.toLocaleString()} km`} />}
                    {job.totalEstimatedCost > 0 && <InfoRow label="Est. Cost" value={`$${Number(job.totalEstimatedCost).toFixed(2)}`} />}
                    {/* ⚠️ NEVER render job.description directly — it is a structured
                        object, and React throws on an object child, which blanks the
                        whole screen. Tapping this row to expand it used to do exactly
                        that. */}
                    {jobDescriptionText(job.description) && (
                        <div className="pt-1">
                            <p className="text-[10px] text-gray-400 uppercase font-semibold mb-0.5">Notes</p>
                            <p className="text-xs text-gray-600 leading-relaxed">{jobDescriptionText(job.description)}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const InfoRow = ({ label, value, isPhone }) => (
    <div className="flex items-center justify-between gap-2 py-0.5">
        <span className="text-[11px] text-gray-400">{label}</span>
        {isPhone ? (
            <a href={`tel:${value}`} className="text-xs font-medium text-blue-600 underline underline-offset-2">{value}</a>
        ) : (
            <span className="text-xs font-medium text-gray-700 truncate text-right max-w-[200px]">{value}</span>
        )}
    </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// ── Main Component ───────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

const MobileJobsDashboard = ({
                                 // 'all' (default) or 'mine'. The phone used to be
                                 // hardcoded to the signed-in user's own jobs, which
                                 // left an owner unable to see their workshop's board.
                                 scope = 'all',
                                 onScopeChange,
                                 jobs,
                                 jobsLoading,
                                 /**
                                  * The all-jobs list comes twenty at a time with a total;
                                  * null for "mine", which is one list. `{page, totalPages,
                                  * total, size, onPage}`.
                                  */
                                 paging = null,
                                 /**
                                  * Told the settled search term (or null) so the page can ask
                                  * the SERVER for it — this list holds twenty of possibly
                                  * thousands. Null for "mine", which is one list already.
                                  */
                                 onSearchTerm = null,
                                 operationLoading,
                                 workCategories,
                                 categoryTypes,
                                 // Callbacks
                                 onRefresh,
                                 onStartJob,
                                 onPauseJob,
                                 onResumeJob,
                                 onOpenComplete,
                                 onAddWorkItem,
                                 onEditWorkItem,
                                 onRemoveWorkItem,
                                 onAddNote,
                                 onRefreshJob,
                             }) => {
    const dispatch = useDispatch();
    const { selectedJob } = useSelector(state => state.jobs);

    const [activeTab, setActiveTab] = useState('in-progress');
    const [openJobUuid, setOpenJobUuid] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    // Local, over the loaded page, by rego or customer name. The server is never asked.
    const [search, setSearch] = useState('');
    /**
     * The same two speeds as the desktop lanes: `search` narrows the loaded page on the
     * keystroke below, and the settled term goes up to the page that fetches — the
     * phone holds twenty of a workshop's jobs, so the car being looked for is usually
     * on another page. Reported up rather than fetched here, because a component
     * renders and the page owns the query.
     */
    const serverSearch = useServerSearchTerm(search);
    useEffect(() => {
        if (onSearchTerm) onSearchTerm(serverSearch.q);
    }, [onSearchTerm, serverSearch.q]);

    // Live clock for SLO timers
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 5000);
        return () => clearInterval(timer);
    }, []);

    // Partition jobs into columns
    const columnData = useMemo(() => {
        const visible = filterByNameOrRego(jobs, search);
        const data = {};
        for (const col of COLUMNS) {
            data[col.id] = visible.filter(j => col.statuses.includes(j.status));
        }
        return data;
    }, [jobs, search]);

    // Stats
    const inProgressJobs = columnData['in-progress'] || [];
    const overdueCount = inProgressJobs.filter(j =>
        j.overdue === true || getSloStatus(getLiveActiveSeconds(j), j.sloBudgetSeconds) === 'overdue'
    ).length;
    const atRiskCount = inProgressJobs.filter(j =>
        !j.overdue && getSloStatus(getLiveActiveSeconds(j), j.sloBudgetSeconds) === 'at-risk'
    ).length;

    const activeColumn = COLUMNS.find(c => c.id === activeTab);
    const activeJobs = columnData[activeTab] || [];

    const handleOpenJob = useCallback((job) => {
        setOpenJobUuid(job.uuid);
        // Fetch full job detail (work items, measurements, notes)
        dispatch(fetchJob(job.uuid));
        dispatch(fetchJobResources(job.uuid));
        dispatch(fetchAvailableResources({ jobUuid: job.uuid }));
    }, [dispatch]);

    const handleCloseJob = useCallback(() => {
        setOpenJobUuid(null);
        // Refresh the list to pick up any changes
        onRefresh();
    }, [onRefresh]);

    // Determine the full job to show (selectedJob from Redux has full detail)
    const detailJob = openJobUuid && selectedJob?.uuid === openJobUuid ? selectedJob : null;

    return (
        // flex-1/min-h-0 rather than min-h-screen: this now renders inside
        // MobileShell's <main>, between the banners and the bottom tab bar. A
        // screen-height child would push the tab bar off the viewport.
        <div className="flex flex-col flex-1 min-h-0 bg-gray-50">
            {/* ── Header ── */}
            <div className="bg-white border-b border-gray-100 px-4 pt-4 pb-3 shrink-0">
                {/* Title row */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 bg-violet-50 rounded-xl flex items-center justify-center">
                            <Wrench size={16} className="text-violet-600" />
                        </div>
                        <div>
                            <h1 className="text-base font-bold text-gray-900 leading-tight">
                                {scope === 'mine' ? 'My Jobs' : 'Jobs'}
                            </h1>
                            <p className="text-[11px] text-gray-400">
                                {/* The server's total when paged — how many there ARE, not
                                    how many this page holds or the search left. */}
                                {currentTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · {paging ? paging.total : jobs.length}
                                {scope === 'mine' ? ' assigned to me' : ' in the workshop'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onRefresh} disabled={jobsLoading}
                            className="p-2.5 active:bg-gray-100 rounded-xl min-w-[44px] min-h-[44px] flex items-center justify-center">
                        <RefreshCw size={18} className={`text-gray-400 ${jobsLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Whose jobs. The whole workshop by default — narrowing to your own is
                    the deliberate act, not the other way round. Rendered only when the
                    parent supplies a handler, so the component still works standalone. */}
                {onScopeChange && (
                    <div className="mb-3">
                        <SegmentedControl
                            ariaLabel="Job scope"
                            options={[
                                { key: 'all',  label: 'All jobs' },
                                { key: 'mine', label: 'Me' },
                            ]}
                            value={scope}
                            onChange={onScopeChange}
                            disabled={jobsLoading}
                        />
                    </div>
                )}

                {/* Find one car on this page; walk the pages when there are more. */}
                <PagedSearchControls
                    className="mb-3"
                    search={search}
                    onSearch={setSearch}
                    page={paging?.page ?? 0}
                    totalPages={paging?.totalPages ?? 1}
                    total={paging?.total ?? jobs.length}
                    size={paging?.size ?? Math.max(1, jobs.length)}
                    onPage={paging?.onPage ?? (() => {})}
                    placeholder="Rego or customer name"
                    searchLabel="Search jobs on this page"
                    pagerLabel="jobs"
                    error={serverSearch.error}
                    alwaysShowPager={!!paging}
                />

                {/* Health strip */}
                <div className="flex items-center gap-2 mb-3">
                    {overdueCount > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle size={11} className="text-red-500" />
                            <span className="text-xs font-bold text-red-700">{overdueCount}</span>
                            <span className="text-[10px] text-red-500">overdue</span>
                        </div>
                    )}
                    {atRiskCount > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200">
                            <span className="text-xs font-bold text-amber-700">{atRiskCount}</span>
                            <span className="text-[10px] text-amber-600">at risk</span>
                        </div>
                    )}
                    {overdueCount === 0 && atRiskCount === 0 && inProgressJobs.length > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-[10px] text-emerald-700 font-medium">All on track</span>
                        </div>
                    )}
                </div>

                {/* Status tabs */}
                <div className="flex gap-1.5">
                    {COLUMNS.map(col => {
                        const count = (columnData[col.id] || []).length;
                        const isActive = col.id === activeTab;
                        return (
                            <button
                                key={col.id}
                                onClick={() => setActiveTab(col.id)}
                                className={`flex-1 py-2 rounded-xl text-center transition-colors min-h-[44px] ${
                                    isActive ? `${col.activeBg} text-white shadow-sm` : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                <span className="block text-sm font-bold leading-tight">{count}</span>
                                <span className="block text-[10px] leading-tight mt-0.5">{col.short}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Job list ── */}
            <PullToRefresh onRefresh={onRefresh} className="flex-1 overflow-y-auto px-4 py-3">
                {/* The wait while a page loads — paging to the next hundred too. "No …
                    jobs" is only said once the load is done. */}
                {jobsLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400" role="status" aria-label="Loading jobs">
                        <RefreshCw size={24} className="animate-spin mb-2" />
                        <span className="text-sm">Loading jobs…</span>
                    </div>
                ) : activeJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-300">
                        <Wrench size={32} className="mb-2 opacity-40" />
                        <p className="text-sm text-gray-400">No {activeColumn?.title?.toLowerCase()} jobs</p>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {activeJobs.map(job => (
                            <JobListCard key={job.uuid} job={job} onOpen={handleOpenJob} />
                        ))}
                    </div>
                )}
            </PullToRefresh>

            {/* ── Full-screen job detail ── */}
            {openJobUuid && detailJob && (
                <MobileJobDetail
                    job={detailJob}
                    operationLoading={operationLoading}
                    workCategories={workCategories}
                    categoryTypes={categoryTypes}
                    onClose={handleCloseJob}
                    onStartJob={onStartJob}
                    onPauseJob={onPauseJob}
                    onResumeJob={onResumeJob}
                    onOpenComplete={onOpenComplete}
                    onAddWorkItem={onAddWorkItem}
                    onEditWorkItem={onEditWorkItem}
                    onRemoveWorkItem={onRemoveWorkItem}
                    onAddNote={onAddNote}
                    onRefreshJob={onRefreshJob}
                />
            )}

            {/* Loading overlay while fetching job detail */}
            {openJobUuid && !detailJob && (
                <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                        <RefreshCw size={28} className="animate-spin" />
                        <span className="text-sm">Loading job…</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileJobsDashboard;